import { useId, type ReactElement } from 'react';
import {
  setAbsoluteTimeFormat,
  type ClockFormat,
  type DateFormat,
} from '@/lib/absoluteTimeFormat';
import { useAbsoluteTimeFormat } from '@/hooks/useAbsoluteTimeFormat';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import { RadioButtonGroup, type RadioButtonOption } from './RadioButtonGroup';
import { ZoneExample } from './ZoneExample';

const DATE_OPTIONS: readonly RadioButtonOption<DateFormat>[] = [
  { value: 'ymd', label: 'YYYY/MM/DD' },
  { value: 'mdy', label: 'Month DD, YYYY' },
  { value: 'dmy', label: 'DD Month YYYY' },
];

const CLOCK_OPTIONS: readonly RadioButtonOption<ClockFormat>[] = [
  { value: '24h', label: '24-hour' },
  { value: '12h', label: '12-hour (AM/PM)' },
];

/**
 * Settings → Preferences → Timestamp Format: the date order and the 12/24-hour
 * clock of every full date and time (Absolute tables, and tooltips and pickers
 * in either mode), with a live example. Reads and saves the stored format
 * itself, so the radios and the example can't disagree.
 */
export function AbsoluteFormatControl(): ReactElement {
  const value = useAbsoluteTimeFormat();
  const timeZone = useDisplayTimeZone();
  const dateLabelId = useId();
  const clockLabelId = useId();
  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-muted-foreground">
        How full dates and times read, in tables and tooltips.
      </p>
      <div>
        <p id={dateLabelId} className="mb-2 text-sm font-medium">
          Date
        </p>
        <RadioButtonGroup
          aria-labelledby={dateLabelId}
          options={DATE_OPTIONS}
          value={value.date}
          onChange={date => setAbsoluteTimeFormat({ ...value, date })}
        />
      </div>
      <div>
        <p id={clockLabelId} className="mb-2 text-sm font-medium">
          Time
        </p>
        <RadioButtonGroup
          aria-labelledby={clockLabelId}
          options={CLOCK_OPTIONS}
          value={value.clock}
          onChange={clock => setAbsoluteTimeFormat({ ...value, clock })}
        />
      </div>
      <ZoneExample zone={timeZone} testId="absolute-example" />
    </div>
  );
}
